import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import {
  acceptProject,
  acceptCustomConsentForms,
  fetchWorkerConsentAcceptances,
  fetchAssignedCompanies,
  getWorkerRequirementStatus,
  resetTempOnboardingData,
  setOnboarding,
} from 'modules/state-mgmt/onboarding';
import { hasWorkerAcceptedTheProject } from 'utils/onboarding';
import { prettifyErrorResponse } from 'utils';
import { WorkerInvitationStatus } from 'models/general';
import { ROUTES } from 'constants/routes';

interface UseProjectAcceptanceParams {
  workerId: string;
  workerUserId: string;
  projectId: string;
  projectName: string;
  languageCode: string;
  getFormPayloads: () => { companyId: string; formPayload: any[] }[];
  isUnverified: boolean;
  isOffline: boolean;
  isWorker: boolean;
  firstStep: () => void;
}

interface AcceptanceOptions {
  consentAgreementIds?: string[];
  consentAgreementContentIds?: string[];
  signatureUrl?: string;
}

export const useProjectAcceptance = ({
  workerId,
  workerUserId,
  projectId,
  languageCode,
  getFormPayloads,
  isUnverified,
  isOffline,
  isWorker,
  firstStep,
}: UseProjectAcceptanceParams) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState<{
    isCertificationComplete: boolean;
    isCoursesComplete: boolean;
    isDrugTestingComplete: boolean;
  } | null>(null);

  const { addEmployerFlow, assignedCompanies, onboarding } = useAppSelector(state => ({
    addEmployerFlow: state.onboarding.temp?.addEmployerFlow || false,
    assignedCompanies: state.onboarding.assignedCompanies || [],
    onboarding: state.onboarding,
  }));

  const isProjectAlreadyAccepted = hasWorkerAcceptedTheProject(assignedCompanies);

  const executeAcceptance = useCallback(
    async (opts?: AcceptanceOptions) => {
      const { consentAgreementIds, consentAgreementContentIds, signatureUrl } = opts || {};

      // Offline path (admin only) — queue for background upload
      if (isOffline && !isWorker) {
        const payloads = getFormPayloads();
        const hasCustomConsent = !!(consentAgreementIds?.length && consentAgreementContentIds?.length);
        dispatch(
          setOnboarding({
            assignedCompanies:
              onboarding.temp?.employers?.map(e => ({
                id: e.value,
                name: e.label,
                workerProjectStatus: 1,
              })) || [],
            temp: {
              ...onboarding.temp,
              consentFormPayloads: payloads,
              ...(hasCustomConsent
                ? { customConsentAcceptances: { consentAgreementIds, consentAgreementContentIds } }
                : {}),
            },
            uploadingDetails: {
              ...onboarding.uploadingDetails,
              isOffline: true,
              errors: { pageUrl: '', errorObject: null },
              pendingUploads: {
                ...onboarding.uploadingDetails?.pendingUploads,
                consentFormData: true,
                ...(hasCustomConsent ? { customConsentAcceptances: true } : {}),
              },
            },
          }),
        );
        enqueueSnackbar(t('consentForm.offlineSubmitSuccess'), { variant: 'success' });
        firstStep();
        return;
      }

      try {
        setIsLoading(true);

        // addEmployerFlow with project already accepted: only sign custom forms, then navigate
        if (addEmployerFlow && isProjectAlreadyAccepted) {
          if (consentAgreementIds?.length && consentAgreementContentIds?.length && signatureUrl) {
            await dispatch(
              acceptCustomConsentForms({
                workerId,
                projectId,
                languageCode,
                consentAgreementContentIds,
                consentAgreementIds,
                signatureUrl,
                source: 'WorkerOnboarding',
              }),
            ).unwrap();
            await dispatch(fetchWorkerConsentAcceptances({ projectId, workerId, languageCode })).unwrap();
          }
          setIsLoading(false);
          // This is the end of the flow: the project was already accepted, so the outstanding
          // custom consent forms were the only thing left to sign. An admin session therefore ends
          // on Select Project, ready for the next worker, the same way the CH-4508 exit below does
          // — including when the worker signed in mid-session to run the add-employer flow, where
          // `isWorker` stays false because the user slice keeps the admin's identity.
          // Product decision, 2026-09-21.
          navigate(isWorker ? ROUTES.PROJECT_LIST.path : ROUTES.SELECT_PROJECT.path);
          return;
        }

        // Normal flow: accept custom forms (if any), then acceptProject per company
        if (consentAgreementIds?.length && consentAgreementContentIds?.length && signatureUrl) {
          await dispatch(
            acceptCustomConsentForms({
              workerId,
              projectId,
              languageCode,
              consentAgreementContentIds,
              consentAgreementIds,
              signatureUrl,
              source: 'WorkerOnboarding',
            }),
          ).unwrap();
        }

        const payloads = getFormPayloads();
        const acceptApis = payloads.map(payload =>
          dispatch(
            acceptProject({
              workerId: workerUserId,
              projectId,
              languageCode,
              data: { consentFormFields: payload.formPayload },
              companyId: payload.companyId,
            }),
          ).unwrap(),
        );
        await Promise.all(acceptApis);

        await dispatch(fetchAssignedCompanies({ workerId, projectId, languageCode })).unwrap();
        consentAgreementIds?.length &&
          consentAgreementContentIds?.length &&
          (await dispatch(fetchWorkerConsentAcceptances({ projectId, workerId, languageCode })).unwrap());

        // CH-4508: In admin mode, skip the Success Screen entirely and go straight to SelectProject.
        // SelectProject's mount effect will call resetOnboardingState for the next worker.
        if (!isWorker) {
          setIsLoading(false);
          // Admin skips the Success Screen, so this toast is their only completion feedback.
          enqueueSnackbar(t('consentForm.registrationSuccess'), { variant: 'success' });
          navigate(ROUTES.SELECT_PROJECT.path);
          return;
        }

        if (isUnverified) {
          setIsLoading(false);
          setOnboardingStatus({ isCertificationComplete: true, isCoursesComplete: true, isDrugTestingComplete: true });
        } else {
          const statusResponse = await dispatch(
            getWorkerRequirementStatus({ workerId, projectId, languageCode }),
          ).unwrap();
          setIsLoading(false);
          setOnboardingStatus({
            isCertificationComplete: statusResponse?.isCertificationsCompleted || false,
            isCoursesComplete: statusResponse?.isCoursesCompleted || false,
            isDrugTestingComplete: statusResponse?.drugTestCompleted || false,
          });
        }

        dispatch(resetTempOnboardingData());
      } catch (error) {
        setIsLoading(false);
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
      }
    },
    [
      addEmployerFlow,
      isProjectAlreadyAccepted,
      isOffline,
      isWorker,
      workerId,
      workerUserId,
      projectId,
      languageCode,
      getFormPayloads,
      isUnverified,
      firstStep,
      onboarding,
      dispatch,
      navigate,
      t,
    ],
  );

  const onCloseWelcomeModal = useCallback(() => {
    if (isWorker) {
      navigate(ROUTES.PROJECT_LIST.path);
    } else {
      firstStep();
      setOnboardingStatus(null);
    }
  }, [isWorker, firstStep, navigate]);

  return { executeAcceptance, onboardingStatus, onCloseWelcomeModal, isLoading };
};
